import * as WebBrowser from 'expo-web-browser';
import { useState } from 'react';
import {
  Image,
  Linking,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { Text } from '@/components/Text';
import { colors, fonts, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import type { ReferMarketing } from '@/services/referralStats';

/**
 * The Refer tab's one dark moment (v5): two athletes, what they made, and
 * a tap into the video.
 *
 * It replaced the dark earnings hero rather than joining it — DESIGN.md
 * allows one dark moment per screen, and proof from someone the user
 * recognises outsells their own balance, which the Home tab already shows.
 *
 * Everything on it is the server's (`ReferMarketing.story`, authored in the
 * backend's ReferIndexCopy): the poster and the video are keys in the
 * assets bucket, and the headline and CTA arrive as text so the card can be
 * spoken and can survive a poster that fails to load. Nothing here is
 * written in the app.
 *
 * The poster is Jason's flattened art — headline, play button and "Watch
 * their story" are pixels — so there is nothing to lay out over it. When
 * the asset becomes a clean photo, the server's `headline`/`cta` get
 * composed here and the fallback below becomes the normal render.
 *
 * No M/P watermark, though brand kit p.16 asks for one on dark cards: the
 * surface is a photograph, and a scaled monogram over two faces reads as a
 * smudge. Documented drift in docs/screens/referrals.md.
 *
 * Without a video URL the poster still draws; only the tap is gated.
 */
export interface StoryCardProps {
  story: ReferMarketing['story'];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export function StoryCard({ story, style, testID }: StoryCardProps) {
  const {
    video_url: videoUrl,
    poster_url: posterUrl,
    accessibility_label: accessibilityLabel,
    headline,
    cta,
  } = story;
  // A poster that fails to download or decode would leave a tappable Deep
  // Green slab with nothing on it. Draw the words instead — and draw them
  // from the start when the server sent no poster at all. The failure is
  // remembered per URL, so a refresh that brings a new poster (or the same
  // one, now reachable) gets to try again instead of staying on text for
  // the rest of the session.
  const [failedPosterUrl, setFailedPosterUrl] = useState<string | null>(null);
  const posterFailed = posterUrl !== null && failedPosterUrl === posterUrl;
  const art =
    posterFailed || !posterUrl ? (
      <View style={styles.fallback} testID={testID ? `${testID}-fallback` : undefined}>
        <Text style={styles.fallbackHeadline}>{headline}</Text>
        {videoUrl ? <Text style={styles.fallbackCta}>{`▶ ${cta}`}</Text> : null}
      </View>
    ) : (
      <Image
        source={{ uri: posterUrl }}
        style={styles.poster}
        resizeMode="cover"
        accessibilityIgnoresInvertColors
        onError={() => setFailedPosterUrl(posterUrl)}
      />
    );

  // The poster always draws — it is the screen's proof, and it carries the
  // headline whether or not there is a video behind it. Only the TAP is
  // gated: with no URL the card is a plain image with no button role, so
  // VoiceOver doesn't announce an action that isn't there and nothing
  // responds to a press. The play glyph is baked into the art, so it still
  // reads as playable to a sighted user until a URL lands — the one thing
  // this state cannot fix without a different poster.
  if (!videoUrl) {
    return (
      <View
        accessible
        accessibilityRole="image"
        accessibilityLabel={accessibilityLabel}
        style={[styles.card, style]}
        testID={testID}
      >
        {art}
      </View>
    );
  }

  return (
    <Pressable
      onPress={() => {
        // Web: a plain link. expo-web-browser's web build opens a fixed
        // 500x650 chromeless popup and reports success even when a popup
        // blocker swallowed it, so the user would get a toolbar-less
        // window at best and silence at worst. Linking hands the URL to
        // the browser like any other link.
        if (Platform.OS === 'web') {
          Linking.openURL(videoUrl).catch(() => {});
          return;
        }
        // Native: the in-app browser sheet (SFSafariViewController), not
        // Linking.openURL: the user stays in MarketPryce and swipes the
        // sheet away when the video ends, instead of landing in Safari
        // with no way back but the app switcher. (Social example videos
        // play inline via expo-video from app 0.1.2, behind canPlayInline().)
        //
        // A failed open is a no-op the user can retry by tapping again; a
        // toast here would outlive the tab they navigated away from.
        WebBrowser.openBrowserAsync(videoUrl, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
          controlsColor: colors.deepGreen,
        }).catch(() => {});
      }}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      style={({ pressed }) => [styles.card, pressed && styles.pressed, style]}
      testID={testID}
    >
      {art}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    // The RATIO lives on the container, not the <Image>. On the image,
    // `width: '100%'` + `aspectRatio` leaves RN sizing from the asset's
    // intrinsic height instead, and a 1426x802 poster renders several
    // screens tall. The container has a resolved width, so the ratio
    // resolves here — and the crop stays identical from a 375pt SE to a
    // 430pt Pro Max.
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: radii.lg,
    overflow: 'hidden',
    // Deep Green shows through while the poster downloads, so the card
    // never flashes white against the Frost page.
    backgroundColor: colors.deepGreen,
    ...shadows.card,
  },
  pressed: { opacity: 0.9 },
  poster: {
    width: '100%',
    height: '100%',
  },
  fallback: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: spacing.lg,
    gap: spacing.sm,
  },
  fallbackHeadline: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xl,
    lineHeight: 26,
    color: colors.frost,
  },
  fallbackCta: {
    fontFamily: fonts.bodySemiBold,
    fontSize: fontSizes.sm,
    lineHeight: 18,
    color: colors.electricGreen,
  },
});
